import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Linkedin, 
  Calendar, 
  TrendingUp, 
  MessageSquare, 
  Users, 
  Settings,
  Plus,
  BarChart3,
  Clock,
  Target
} from "lucide-react";
import { LinkedInConnectionStatus } from "./components/LinkedInConnectionStatus";
import { ContentScheduler } from "./components/ContentScheduler";
import { EngagementAnalytics } from "./components/EngagementAnalytics";
import { InMailManager } from "./components/InMailManager";
import { AutomationRules } from "./components/AutomationRules";

export const LinkedInMarketingDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [isConnected, setIsConnected] = useState(false);

  const stats = [
    {
      title: "Posts Scheduled",
      value: "12",
      change: "+2 this week",
      icon: Calendar,
      color: "text-blue-600"
    },
    {
      title: "Total Engagement",
      value: "1,247",
      change: "+15% from last month",
      icon: TrendingUp,
      color: "text-green-600"
    },
    {
      title: "InMail Responses",
      value: "89%",
      change: "Response rate",
      icon: MessageSquare,
      color: "text-purple-600"
    },
    {
      title: "New Connections",
      value: "156",
      change: "+23 this week",
      icon: Users,
      color: "text-orange-600"
    }
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Linkedin className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">LinkedIn Marketing</h1>
            <p className="text-muted-foreground">Manage your LinkedIn presence and automation</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LinkedInConnectionStatus 
            isConnected={isConnected} 
            onConnectionChange={setIsConnected} 
          />
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {isConnected && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.change}</p>
                    </div>
                    <IconComponent className={`h-8 w-8 ${stat.color}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="scheduler" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Content
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="inmail" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            InMail
          </TabsTrigger>
          <TabsTrigger value="automation" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Automation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {!isConnected ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-blue-50 rounded-full">
                    <Linkedin className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Connect Your LinkedIn Account</h3>
                    <p className="text-muted-foreground">Get started by connecting your LinkedIn account to access marketing features</p>
                  </div>
                  <LinkedInConnectionStatus 
                    isConnected={isConnected} 
                    onConnectionChange={setIsConnected} 
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>Your latest LinkedIn marketing activities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { action: "Posted: 'The Future of Recruiting'", time: "2 hours ago", status: "published" },
                      { action: "InMail sent to John Doe", time: "4 hours ago", status: "sent" },
                      { action: "Connection request to Jane Smith", time: "1 day ago", status: "pending" },
                    ].map((activity, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm">{activity.action}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {activity.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{activity.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6 text-center">
                    <Plus className="h-8 w-8 mx-auto mb-3 text-blue-600" />
                    <h3 className="font-semibold mb-2">Create Post</h3>
                    <p className="text-sm text-muted-foreground mb-4">Schedule a new LinkedIn post</p>
                    <Button size="sm" onClick={() => setActiveTab("scheduler")}>
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6 text-center">
                    <MessageSquare className="h-8 w-8 mx-auto mb-3 text-purple-600" />
                    <h3 className="font-semibold mb-2">Send InMail</h3>
                    <p className="text-sm text-muted-foreground mb-4">Reach out to prospects</p>
                    <Button size="sm" onClick={() => setActiveTab("inmail")}>
                      Compose
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6 text-center">
                    <Target className="h-8 w-8 mx-auto mb-3 text-green-600" />
                    <h3 className="font-semibold mb-2">Set Automation</h3>
                    <p className="text-sm text-muted-foreground mb-4">Automate your outreach</p>
                    <Button size="sm" onClick={() => setActiveTab("automation")}>
                      Configure
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scheduler">
          <ContentScheduler isConnected={isConnected} />
        </TabsContent>

        <TabsContent value="analytics">
          <EngagementAnalytics isConnected={isConnected} />
        </TabsContent>

        <TabsContent value="inmail">
          <InMailManager isConnected={isConnected} />
        </TabsContent>

        <TabsContent value="automation">
          <AutomationRules isConnected={isConnected} />
        </TabsContent>
      </Tabs>
    </div>
  );
};